import React, { useState, useEffect, useRef } from 'react';
import { Button, Input } from '../../../components';
import { RxCross2 } from 'react-icons/rx';
import { useOffer } from '../../../context/offersContext/OfferContextProvider';

const CreateOffer = ({ showOffersForm, offerToEdit = null }) => {
  const { createOffer, updateOffer } = useOffer();
  const [offer, setOffer] = useState(offerToEdit ? offerToEdit.offer : '');
  const [file, setFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(
    offerToEdit ? offerToEdit.imageUrl : '',
  );
  const [imagePath, setImagePath] = useState(
    offerToEdit ? offerToEdit.imagePath : '',
  );
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null); // Create a ref for the file input

  useEffect(() => {
    if (offerToEdit) {
      setOffer(offerToEdit.offer);
      setImageUrl(offerToEdit.imageUrl || '');
      setImagePath(offerToEdit.imagePath || '');
      setErrors({}); // Reset errors when offerToEdit changes
    }
  }, [offerToEdit]);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setImageUrl(URL.createObjectURL(selectedFile));
      setImagePath(selectedFile.name); // Update the file path with the new file name
      setErrors((prevErrors) => ({ ...prevErrors, file: null })); // Reset file error
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const trimmedOffer = offer.trim();
    if (!trimmedOffer) {
      newErrors.offer = 'Offer is required';
    } else if (offer !== trimmedOffer) {
      newErrors.offer = 'Leading or trailing spaces are not allowed';
    }
    if (!file && !imageUrl) newErrors.file = 'Image is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;
    const formData = new FormData();
    formData.append('offer', offer.trim());
    if (file) {
      formData.append('image', file);
    }
    if (offerToEdit && imagePath) {
      formData.append('imagePath', imagePath.trim());
    }

    try {
      if (offerToEdit) {
        await updateOffer(offerToEdit.id, formData, showOffersForm);
      } else {
        await createOffer(formData, showOffersForm);
      }
    } catch (error) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        submit: 'Failed to submit the offer. Please try again.',
      }));
    }
  };

  return (
    <form className="m-10 bg-slate-50 rounded-2xl p-5" onSubmit={handleSubmit}>
      <div className="font-600 text-2xl flex justify-between">
        <div>{offerToEdit ? 'Update Offer' : 'Create Offer'}</div>
        <div onClick={showOffersForm} className="hover:cursor-pointer">
          <RxCross2 className="hover:rotate-12" />
        </div>
      </div>
      <div className="flex flex-col items-center grid-cols-1">
        <div className="w-96">
          <Input
            label="Offer"
            placeholder="offer"
            value={offer}
            onChange={(e) => setOffer(e.target.value)}
            error={errors.offer}
            required
          />
        </div>
        <div className="w-96">
          {imageUrl && (
            <div className="relative border border-gray-300 rounded-md">
              <img
                src={imageUrl}
                alt="Offer"
                className="w-full h-40 object-cover rounded-md"
                onError={() => {
                  setErrors((prevErrors) => ({
                    ...prevErrors,
                    file: 'Invalid uploaded image URL or file. Upload again',
                  }));
                  setImageUrl(null);
                  setFile(null);
                }}
              />
              <div className="mt-2 text-gray-600 text-sm">
                <span className="font-800 font-bold">File path: &nbsp;</span>
                {imagePath}
              </div>
            </div>
          )}
          <Input
            ref={fileInputRef} // Use the ref here
            id="fileInput"
            label="Upload Image"
            type="file"
            accept=".jpg,.png,.jpeg,.avif"
            required
            onChange={(e) => {
              const selectedFile = e.target.files[0];
              if (selectedFile && selectedFile.size > 5 * 1024 * 1024) {
                setErrors((prevErrors) => ({
                  ...prevErrors,
                  file: 'File size should not exceed 5MB',
                }));
                setFile(null);
                setImageUrl(null);
                setImagePath('');
                fileInputRef.current.value = null; // Reset the file input
              } else {
                setErrors((prevErrors) => ({ ...prevErrors, file: null }));
                handleFileChange(e);
              }
            }}
            error={errors.file}
            multiple={false} // Ensure only one file can be selected
          />
        </div>
        <div className="flex gap-5 mt-5">
          <Button className="secondary" onClick={showOffersForm}>
            Cancel
          </Button>
          <Button
            type="submit"
            className="px-4 py-2 bg-yellow-500 text-white rounded-3xl"
          >
            {offerToEdit ? 'Update' : 'Submit'}
          </Button>
        </div>
        {errors.submit && (
          <div className="text-red-500 mt-2">{errors.submit}</div>
        )}
      </div>
    </form>
  );
};

export default CreateOffer;
